import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiPrinter, FiSearch, FiDownload, FiCalendar, FiFileText,
  FiCheckSquare, FiSquare, FiEye, FiX, FiChevronLeft, FiChevronRight,
  FiExternalLink
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import useReportMemory from '../../hooks/useReportMemory';
import PageLayout from '../../components/PageLayout';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { addPrintBrand } from '../../utils/printBranding';

// ── Transaction type labels ──
const TRAN_TYPE_LABELS = {
  OB: 'Opening Balance', PO: 'Purchase Order', GRN: 'Goods Receipt Note',
  PI: 'Purchase Invoice', NPI: 'Non-Tax Purchase Invoice',
  PR: 'Purchase Return', NPR: 'Non-Tax Purchase Return',
  QT: 'Quotation', SO: 'Sale Order', DC: 'Delivery Challan',
  SI: 'Sale Invoice', NSI: 'Non-Tax Sale Invoice',
  PS: 'POS Sale', SR: 'Sale Return', NSR: 'Non-Tax Sale Return',
  CR: 'Cash Receipt', BR: 'Bank Receipt',
  CP: 'Cash Payment', BP: 'Bank Payment', JV: 'Journal Voucher',
  AS: 'Add Stock', RS: 'Reduce Stock', ST: 'Stock Transfer'
};

// ── Voucher title mapping (for print header) ──
const VOUCHER_TITLES = {
  CP: 'CASH PAYMENT VOUCHER', BP: 'BANK PAYMENT VOUCHER',
  CR: 'CASH RECEIPT VOUCHER', BR: 'BANK RECEIPT VOUCHER',
  JV: 'JOURNAL VOUCHER', SI: 'SALE INVOICE', NSI: 'NON-TAX SALE INVOICE',
  PS: 'POS SALE INVOICE',
  PI: 'PURCHASE INVOICE', NPI: 'NON-TAX PURCHASE INVOICE',
  SR: 'SALE RETURN NOTE', NSR: 'NON-TAX SALE RETURN NOTE',
  PR: 'PURCHASE RETURN NOTE', NPR: 'NON-TAX PURCHASE RETURN NOTE',
  GRN: 'GOODS RECEIPT NOTE', PO: 'PURCHASE ORDER', QT: 'QUOTATION',
  SO: 'SALE ORDER', DC: 'DELIVERY CHALLAN', OB: 'OPENING BALANCE',
  AS: 'ADD STOCK VOUCHER', RS: 'REDUCE STOCK VOUCHER', ST: 'STOCK TRANSFER VOUCHER'
};

// ── Voucher type filter groups ──
const VOUCHER_FILTER_OPTIONS = [
  { value: 'all', label: 'All Voucher Types' },
  { value: 'CP', label: 'Cash Payment (CP)' },
  { value: 'BP', label: 'Bank Payment (BP)' },
  { value: 'CR', label: 'Cash Receipt (CR)' },
  { value: 'BR', label: 'Bank Receipt (BR)' },
  { value: 'JV', label: 'Journal Voucher (JV)' },
  { value: 'SI', label: 'Sale Invoice (SI)' },
  { value: 'NSI', label: 'Non-Tax Sale Invoice (NSI)' },
  { value: 'PS', label: 'POS Sale (PS)' },
  { value: 'PI', label: 'Purchase Invoice (PI)' },
  { value: 'NPI', label: 'Non-Tax Purchase Invoice (NPI)' },
  { value: 'SR', label: 'Sale Return (SR)' },
  { value: 'NSR', label: 'Non-Tax Sale Return (NSR)' },
  { value: 'PR', label: 'Purchase Return (PR)' },
  { value: 'NPR', label: 'Non-Tax Purchase Return (NPR)' },
  { value: 'GRN', label: 'Goods Receipt (GRN)' },
  { value: 'PO', label: 'Purchase Order (PO)' },
  { value: 'QT', label: 'Quotation (QT)' },
  { value: 'SO', label: 'Sale Order (SO)' },
  { value: 'DC', label: 'Delivery Challan (DC)' },
  { value: 'OB', label: 'Opening Balance (OB)' },
  { value: 'AS', label: 'Add Stock (AS)' },
  { value: 'RS', label: 'Reduce Stock (RS)' },
  { value: 'ST', label: 'Stock Transfer (ST)' }
];

// ── Status filter options ──
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'Posted', label: 'Posted' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'Approved', label: 'Approved' }
];

// ── Number to words helper ──
const numberToWords = (num) => {
  if (num === 0) return 'Zero Only';
  if (num === null || num === undefined || isNaN(num)) return '';

  const isNegative = num < 0;
  num = Math.abs(num);

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const intPart = Math.floor(num);
  const decPart = Math.round((num - intPart) * 100);

  const convertBelowThousand = (n) => {
    let str = '';
    if (n >= 100) {
      str += ones[Math.floor(n / 100)] + ' Hundred';
      n %= 100;
      if (n > 0) str += ' and ';
    }
    if (n >= 20) {
      str += tens[Math.floor(n / 10)];
      n %= 10;
      if (n > 0) str += ' ' + ones[n];
    } else if (n > 0) {
      str += ones[n];
    }
    return str;
  };

  // Using South Asian numbering: Crore, Lakh, Thousand
  let result = '';
  if (intPart >= 10000000) {
    result += convertBelowThousand(Math.floor(intPart / 10000000)) + ' Crore ';
    num = intPart % 10000000;
  } else {
    num = intPart;
  }
  if (num >= 100000) {
    result += convertBelowThousand(Math.floor(num / 100000)) + ' Lakh ';
    num %= 100000;
  }
  if (num >= 1000) {
    result += convertBelowThousand(Math.floor(num / 1000)) + ' Thousand ';
    num %= 1000;
  }
  if (num > 0) {
    result += convertBelowThousand(num);
  }

  result = result.trim();
  if (!result) result = 'Zero';

  if (decPart > 0) {
    result += ' and ' + convertBelowThousand(decPart) + ' Paisa';
  }

  return (isNegative ? 'Minus ' : '') + result + ' Only';
};

// ── Format helpers ──
const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', { minimumFractionDigits: dp, maximumFractionDigits: dp });
};

const formatDate = (v) => fmtDate(v) || '-';

// ── Pagination constant ──
const PAGE_SIZE = 25;

// ══════════════════════════════════════════════
// ██  MAIN COMPONENT
// ══════════════════════════════════════════════
const Vouchers = () => {
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const [savedMem, saveMemory] = useReportMemory('vouchers');
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const currencyCode = activeCompany?.currencyCode || 'PKR';
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';
  const navigate = useNavigate();

  // Data state
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [subTransactionsMap, setSubTransactionsMap] = useState(() => savedMem?.subTransactionsMap ?? {});
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(() => savedMem?.hasLoaded ?? false); // Tier 5: gated — no read until "Generate"
  const [loadingSubTrans, setLoadingSubTrans] = useState(false);

  // Filter state
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [voucherType, setVoucherType] = useState(() => savedMem?.voucherType ?? 'all');
  const [taxFilter, setTaxFilter] = useState(() => savedMem?.taxFilter ?? 'both');
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'all');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(() => savedMem?.selectedIds ?? new Set());

  // Modal state for voucher detail view
  const [viewVoucher, setViewVoucher] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // ── Load Financial Years ──
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // On a restored view keep the snapshot's selected FY (don't reset to active).
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  useEffect(() => { loadFinancialYears(); }, [loadFinancialYears]);

  // ── Load Transactions ──
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id || !selectedFYId) return;
    try {
      setLoading(true);
      const tranRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const conditions = [where('financialYearId', '==', selectedFYId)];

      const q = query(tranRef, ...conditions);
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const allTrans = snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Sort by docDate descending
      allTrans.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(allTrans);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, activeBranch]);

  // Tier 5 (Firebase cost): NO auto-load. This report used to read the WHOLE
  // financial year's transactions on mount. It now loads only when the user
  // clicks "Generate", so simply opening the page costs nothing.
  const generateReport = useCallback(() => {
    if (!selectedFYId) return;
    setHasLoaded(true);
    setSelectedIds(new Set());
    setCurrentPage(1);
    loadTransactions();
  }, [selectedFYId, loadTransactions]);

  // ── Load SubTransactions for a specific transaction ──
  const loadSubTransactions = useCallback(async (transactionId) => {
    if (!activeCompany?.id || subTransactionsMap[transactionId]) return;
    try {
      setLoadingSubTrans(true);
      const subRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const q = query(subRef, where('transactionId', '==', transactionId));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const subs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      subs.sort((a, b) => (b.debitAmount || 0) - (a.debitAmount || 0));
      setSubTransactionsMap(prev => ({ ...prev, [transactionId]: subs }));
    } catch (error) {
      console.error('Error loading sub transactions:', error);
    } finally {
      setLoadingSubTrans(false);
    }
  }, [activeCompany, subTransactionsMap]);

  // ── Filtered transactions ──
  const filteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    if (voucherType !== 'all') {
      filtered = filtered.filter(t => t.tranTypeCode === voucherType);
    }
    if (taxFilter === 'tax') {
      filtered = filtered.filter(t => !['NSI', 'NSR', 'NPI', 'NPR'].includes(t.tranTypeCode));
    } else if (taxFilter === 'nonTax') {
      filtered = filtered.filter(t => !['SI', 'SR', 'PI', 'PR'].includes(t.tranTypeCode));
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.docNumber?.toLowerCase().includes(q) ||
        t.partyName?.toLowerCase().includes(q) ||
        t.notes?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [transactions, voucherType, taxFilter, statusFilter, dateFrom, dateTo, searchQuery]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [voucherType, statusFilter, dateFrom, dateTo, searchQuery]);

  // ── Paginated data ──
  const totalPages = Math.max(1, Math.ceil(filteredTransactions.length / PAGE_SIZE));
  const paginatedTransactions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredTransactions.slice(start, start + PAGE_SIZE);
  }, [filteredTransactions, currentPage]);

  // ── Summary ──
  const summary = useMemo(() => {
    const totalDebit = filteredTransactions.reduce((s, t) => s + (t.grossAmount || t.subtotal || t.totalAmount || 0), 0);
    const totalCredit = filteredTransactions.reduce((s, t) => s + (t.totalAmount || 0), 0);
    const netAmount = filteredTransactions.reduce((s, t) => s + (t.totalAmount || 0), 0);
    return {
      totalVouchers: filteredTransactions.length,
      totalDebit,
      totalCredit,
      netAmount
    };
  }, [filteredTransactions]);

  // ── Backward memory ── save the view snapshot (filters + fetched rows) once a
  // report has been generated, so Back restores it WITHOUT re-fetching. Filters
  // are applied client-side via useMemo, so this is display-only (no re-Generate).
  useEffect(() => {
    if (!hasLoaded) return;
    saveMemory({
      selectedFYId, dateFrom, dateTo, voucherType, taxFilter, statusFilter, searchQuery,
      transactions, subTransactionsMap, selectedIds, hasLoaded
    });
  }, [selectedFYId, dateFrom, dateTo, voucherType, taxFilter, statusFilter, searchQuery,
      transactions, subTransactionsMap, selectedIds, hasLoaded, saveMemory]);

  // ── Bulk Selection ──
  const allOnPageSelected = paginatedTransactions.length > 0 &&
    paginatedTransactions.every(t => selectedIds.has(t.id));

  const toggleSelectAll = () => {
    const next = new Set(selectedIds);
    if (allOnPageSelected) {
      paginatedTransactions.forEach(t => next.delete(t.id));
    } else {
      paginatedTransactions.forEach(t => next.add(t.id));
    }
    setSelectedIds(next);
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  // ── Get status badge color ──
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Posted': return { bg: '#E8F5E9', color: '#2E7D32' };
      case 'Draft': return { bg: '#FFF3E0', color: '#E65100' };
      case 'Cancelled': return { bg: '#FFEBEE', color: '#C62828' };
      case 'Approved': return { bg: '#E3F2FD', color: '#1565C0' };
      default: return { bg: colors.neutral[100], color: colors.text.secondary };
    }
  };

  // ── View Voucher Detail ──
  const handleViewVoucher = async (transaction) => {
    setViewVoucher(transaction);
    await loadSubTransactions(transaction.id);
  };

  // ══════════════════════════════════════════
  // ██  PRINT SINGLE VOUCHER
  // ══════════════════════════════════════════
  const printVoucher = async (transaction) => {
    // Make sure sub-transactions are loaded
    if (!subTransactionsMap[transaction.id]) {
      await loadSubTransactions(transaction.id);
    }

    // Small delay to ensure state update if subTransactions just loaded
    setTimeout(() => {
      const subs = subTransactionsMap[transaction.id] || [];
      const totalDebit = subs.reduce((s, st) => s + (st.debitAmount || 0), 0);
      const totalCredit = subs.reduce((s, st) => s + (st.creditAmount || 0), 0);
      const primaryAmount = transaction.netAmount || transaction.totalAmount || totalDebit || 0;
      const voucherTitle = VOUCHER_TITLES[transaction.tranTypeCode] || 'VOUCHER';

      // Determine Pay To / Received From label
      let partyLabel = 'Party';
      if (['CP', 'BP'].includes(transaction.tranTypeCode)) partyLabel = 'Pay To';
      else if (['CR', 'BR'].includes(transaction.tranTypeCode)) partyLabel = 'Received From';
      else if (['SI', 'NSI', 'PS', 'DC', 'SO', 'QT', 'SR', 'NSR'].includes(transaction.tranTypeCode)) partyLabel = 'Customer';
      else if (['PI', 'NPI', 'GRN', 'PO', 'PR', 'NPR'].includes(transaction.tranTypeCode)) partyLabel = 'Vendor';

      const w = window.open('', '_blank');
      if (!w) return;

      w.document.write(`<!DOCTYPE html><html><head><title>${voucherTitle} - ${transaction.docNumber || ''}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', 'Roboto', sans-serif; margin: 0; color: #1A1A1A; font-size: 13px; background: #f5f5f5; }
          .page { width: 210mm; min-height: 297mm; margin: 0 auto; background: white; padding: 20mm 18mm; position: relative; }
          .border-box { border: 2px solid #800020; padding: 20px; min-height: calc(297mm - 44mm); }

          .company-header { text-align: center; margin-bottom: 18px; padding-bottom: 14px; border-bottom: 2px solid #800020; }
          .company-name { font-size: 22px; font-weight: 700; color: #800020; letter-spacing: 1px; margin-bottom: 4px; }
          .company-address { font-size: 11px; color: #666; line-height: 1.5; }

          .voucher-title { text-align: center; margin: 18px 0 20px; }
          .voucher-title h2 { font-size: 17px; font-weight: 700; color: #800020; letter-spacing: 2px; text-transform: uppercase;
            padding: 6px 20px; display: inline-block; border: 1.5px solid #800020; background: #800020; color: white; }

          .voucher-meta { display: flex; justify-content: space-between; margin-bottom: 18px; font-size: 13px; }
          .voucher-meta .left, .voucher-meta .right { display: flex; flex-direction: column; gap: 4px; }
          .voucher-meta .right { text-align: right; }
          .meta-label { font-weight: 600; color: #333; }
          .meta-value { color: #555; }

          .party-section { margin-bottom: 18px; padding: 10px 14px; background: #fafafa; border: 1px solid #eee; border-radius: 4px; }
          .party-label { font-size: 11px; font-weight: 600; color: #800020; text-transform: uppercase; letter-spacing: 0.5px; }
          .party-name { font-size: 15px; font-weight: 600; color: #1A1A1A; margin-top: 2px; }

          table.entries { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
          table.entries th { background: #800020; color: white; padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 600;
            text-transform: uppercase; letter-spacing: 0.5px; }
          table.entries th.amount { text-align: right; }
          table.entries td { padding: 8px 12px; border-bottom: 1px solid #e0e0e0; font-size: 12px; }
          table.entries td.amount { text-align: right; font-family: 'Consolas', 'Courier New', monospace; font-size: 12px; }
          table.entries tr:last-child td { border-bottom: none; }
          table.entries tbody tr:nth-child(even) { background: #fafafa; }

          .totals-row { background: #f5f0f0 !important; }
          .totals-row td { font-weight: 700; font-size: 13px; border-top: 2px solid #800020; padding: 10px 12px; }

          .amount-words { margin: 16px 0; padding: 10px 14px; background: #f9f5f5; border: 1px solid #e0d0d0; border-radius: 4px; }
          .amount-words .label { font-size: 11px; font-weight: 600; color: #800020; text-transform: uppercase; letter-spacing: 0.5px; }
          .amount-words .text { font-size: 13px; font-weight: 500; color: #333; margin-top: 2px; font-style: italic; }

          .remarks-section { margin-bottom: 20px; }
          .remarks-label { font-size: 11px; font-weight: 600; color: #800020; text-transform: uppercase; letter-spacing: 0.5px; }
          .remarks-text { font-size: 12px; color: #555; margin-top: 4px; }

          ${transaction.lines && transaction.lines.length > 0 ? `
          .items-section { margin-bottom: 18px; }
          .items-section h3 { font-size: 13px; font-weight: 600; color: #800020; margin-bottom: 8px; text-transform: uppercase; }
          table.items { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          table.items th { background: #f0e0e0; color: #800020; padding: 7px 10px; text-align: left; font-size: 10px; font-weight: 600;
            text-transform: uppercase; border-bottom: 1.5px solid #800020; }
          table.items th.amount { text-align: right; }
          table.items td { padding: 6px 10px; border-bottom: 1px solid #eee; font-size: 11px; }
          table.items td.amount { text-align: right; font-family: 'Consolas', monospace; }
          table.items tfoot td { font-weight: 700; border-top: 1.5px solid #800020; font-size: 12px; padding: 8px 10px; }
          ` : ''}

          .signatures { display: flex; justify-content: space-between; margin-top: 50px; padding-top: 10px; }
          .signature-block { text-align: center; min-width: 140px; }
          .signature-line { border-top: 1.5px solid #333; margin-bottom: 6px; width: 140px; }
          .signature-label { font-size: 11px; color: #666; font-weight: 500; }

          .footer-note { text-align: center; margin-top: 30px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 10px; }

          @media print {
            body { background: white; }
            .page { margin: 0; padding: 15mm; box-shadow: none; }
            .no-print { display: none; }
          }
          @media screen {
            .page { box-shadow: 0 2px 20px rgba(0,0,0,0.15); margin: 20px auto; }
            .print-bar { text-align: center; padding: 12px; background: #800020; position: sticky; top: 0; z-index: 100; }
            .print-bar button { padding: 8px 24px; font-size: 14px; font-weight: 600; color: white; background: transparent;
              border: 2px solid white; border-radius: 4px; cursor: pointer; margin: 0 6px; }
            .print-bar button:hover { background: rgba(255,255,255,0.15); }
          }
        </style></head><body>
        <div class="print-bar no-print">
          <button onclick="window.print()">Print Voucher</button>
          <button onclick="window.close()">Close</button>
        </div>
        <div class="page">
          <div class="border-box">
            <div class="company-header">
              <div class="company-name">${activeCompany?.companyName || 'Company Name'}</div>
              <div class="company-address">
                ${activeCompany?.address || ''}${activeCompany?.city ? ', ' + activeCompany.city : ''}${activeCompany?.country ? ', ' + activeCompany.country : ''}
                ${activeCompany?.phone ? '<br>Phone: ' + activeCompany.phone : ''}${activeCompany?.email ? ' | Email: ' + activeCompany.email : ''}
                ${activeCompany?.ntn ? '<br>' + ((activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'CR' : 'NTN') + ': ' + activeCompany.ntn : ''}${activeCompany?.strn ? ' | ' + ((activeCompany?.taxLabel || 'GST').toUpperCase() === 'VAT' ? 'VAT #' : 'STRN') + ': ' + activeCompany.strn : ''}
              </div>
            </div>

            <div class="voucher-title"><h2>${voucherTitle}</h2></div>

            <div class="voucher-meta">
              <div class="left">
                <div><span class="meta-label">Voucher No:</span> <span class="meta-value">${transaction.docNumber || '-'}</span></div>
                ${transaction.jobName ? `<div><span class="meta-label">Job/Project:</span> <span class="meta-value">${transaction.jobName}</span></div>` : ''}
                ${transaction.employeeName ? `<div><span class="meta-label">Employee:</span> <span class="meta-value">${transaction.employeeName}</span></div>` : ''}
              </div>
              <div class="right">
                <div><span class="meta-label">Date:</span> <span class="meta-value">${formatDate(transaction.docDate)}</span></div>
                <div><span class="meta-label">Status:</span> <span class="meta-value">${transaction.status || '-'}</span></div>
              </div>
            </div>

            ${transaction.partyName ? `
            <div class="party-section">
              <div class="party-label">${partyLabel}</div>
              <div class="party-name">${transaction.partyName}</div>
            </div>` : ''}

            ${transaction.lines && transaction.lines.length > 0 ? `
            <div class="items-section">
              <h3>Item Details</h3>
              <table class="items">
                <thead><tr>
                  <th style="width:5%">#</th>
                  <th style="width:12%">Code</th>
                  <th style="width:30%">Item</th>
                  <th class="amount" style="width:10%">Qty</th>
                  <th class="amount" style="width:13%">Unit Price</th>
                  <th class="amount" style="width:10%">Disc</th>
                  <th class="amount" style="width:10%">${taxLabel}</th>
                  <th class="amount" style="width:13%">Amount</th>
                </tr></thead>
                <tbody>
                  ${transaction.lines.map((line, i) => `
                  <tr>
                    <td>${i + 1}</td>
                    <td>${line.itemCode || '-'}</td>
                    <td>${line.itemName || '-'}</td>
                    <td class="amount">${line.quantity != null ? Number(line.quantity).toLocaleString() : '-'}</td>
                    <td class="amount">${formatAmount(line.unitPrice)}</td>
                    <td class="amount">${line.discountAmount ? formatAmount(line.discountAmount) : '-'}</td>
                    <td class="amount">${line.gstAmount ? formatAmount(line.gstAmount) : '-'}</td>
                    <td class="amount">${formatAmount(line.amount)}</td>
                  </tr>`).join('')}
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3" style="text-align:right;">Totals</td>
                    <td class="amount">${transaction.lines.reduce((s, l) => s + (l.quantity || 0), 0).toLocaleString()}</td>
                    <td></td>
                    <td class="amount">${transaction.totalDiscount ? formatAmount(transaction.totalDiscount) : '-'}</td>
                    <td class="amount">${transaction.totalGST ? formatAmount(transaction.totalGST) : '-'}</td>
                    <td class="amount">${formatAmount(transaction.totalAmount)}</td>
                  </tr>
                  ${transaction.totalWHT ? `<tr><td colspan="7" style="text-align:right;">WHT</td><td class="amount">(${formatAmount(transaction.totalWHT)})</td></tr>` : ''}
                  ${(transaction.netAmount && transaction.netAmount !== transaction.totalAmount) ? `<tr><td colspan="7" style="text-align:right;font-size:13px;"><strong>Net Amount</strong></td><td class="amount" style="font-size:13px;"><strong>${formatAmount(transaction.netAmount)}</strong></td></tr>` : ''}
                </tfoot>
              </table>
            </div>` : ''}

            <table class="entries">
              <thead><tr>
                <th style="width:10%">A/C Code</th>
                <th style="width:35%">Account Name</th>
                <th style="width:20%">Description</th>
                <th class="amount" style="width:17.5%">Debit (${currencySymbol})</th>
                <th class="amount" style="width:17.5%">Credit (${currencySymbol})</th>
              </tr></thead>
              <tbody>
                ${subs.length > 0 ? subs.map(st => `
                <tr>
                  <td>${st.accountCode || '-'}</td>
                  <td>${st.accountName || '-'}</td>
                  <td>${st.description || '-'}</td>
                  <td class="amount">${st.debitAmount > 0 ? formatAmount(st.debitAmount) : '-'}</td>
                  <td class="amount">${st.creditAmount > 0 ? formatAmount(st.creditAmount) : '-'}</td>
                </tr>`).join('') : `
                <tr><td colspan="5" style="text-align:center;color:#999;padding:20px;">No accounting entries found</td></tr>`}
              </tbody>
              ${subs.length > 0 ? `
              <tfoot>
                <tr class="totals-row">
                  <td colspan="3" style="text-align:right;">Total</td>
                  <td class="amount">${formatAmount(totalDebit)}</td>
                  <td class="amount">${formatAmount(totalCredit)}</td>
                </tr>
              </tfoot>` : ''}
            </table>

            <div class="amount-words">
              <div class="label">Amount in Words</div>
              <div class="text">Rupees ${numberToWords(primaryAmount)}</div>
            </div>

            ${transaction.notes ? `
            <div class="remarks-section">
              <div class="remarks-label">Remarks / Narration</div>
              <div class="remarks-text">${transaction.notes}</div>
            </div>` : ''}

            <div class="signatures">
              <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Prepared By</div></div>
              <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Checked By</div></div>
              <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Approved By</div></div>
              <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Received By</div></div>
            </div>

            <div class="footer-note">This is a computer-generated voucher. Printed on ${new Date().toLocaleDateString('en-PK', { day: '2-digit', month: 'long', year: 'numeric' })}.</div>
          </div>
        </div>
      </body></html>`);
      addPrintBrand(w);
      w.document.close();
    }, 100);
  };

  // ══════════════════════════════════════════
  // ██  BULK PRINT
  // ══════════════════════════════════════════
  const handleBulkPrint = async () => {
    if (selectedIds.size === 0) return;

    // Load sub-transactions for all selected that are not yet loaded
    const selectedTrans = filteredTransactions.filter(t => selectedIds.has(t.id));
    const toLoad = selectedTrans.filter(t => !subTransactionsMap[t.id]);

    if (toLoad.length > 0) {
      setLoadingSubTrans(true);
      try {
        const subRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
        // Batch load for each missing transaction
        const loadPromises = toLoad.map(async (t) => {
          const q = query(subRef, where('transactionId', '==', t.id));
          const snapshot = await getDocs(q);
          recordReads(activeCompany.id, snapshot.size);
          const subs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
          subs.sort((a, b) => (b.debitAmount || 0) - (a.debitAmount || 0));
          return { id: t.id, subs };
        });
        const results = await Promise.all(loadPromises);
        const newMap = { ...subTransactionsMap };
        results.forEach(r => { newMap[r.id] = r.subs; });
        setSubTransactionsMap(newMap);

        // Proceed after state is updated
        setTimeout(() => printMultipleVouchers(selectedTrans, newMap), 150);
      } catch (error) {
        console.error('Error loading sub-transactions for bulk print:', error);
      } finally {
        setLoadingSubTrans(false);
      }
    } else {
      printMultipleVouchers(selectedTrans, subTransactionsMap);
    }
  };

  const printMultipleVouchers = (transList, subsMap) => {
    const w = window.open('', '_blank');
    if (!w) return;

    const voucherPages = transList.map(transaction => {
      const subs = subsMap[transaction.id] || [];
      const totalDebit = subs.reduce((s, st) => s + (st.debitAmount || 0), 0);
      const totalCredit = subs.reduce((s, st) => s + (st.creditAmount || 0), 0);
      const primaryAmount = transaction.netAmount || transaction.totalAmount || totalDebit || 0;
      const voucherTitle = VOUCHER_TITLES[transaction.tranTypeCode] || 'VOUCHER';

      let partyLabel = 'Party';
      if (['CP', 'BP'].includes(transaction.tranTypeCode)) partyLabel = 'Pay To';
      else if (['CR', 'BR'].includes(transaction.tranTypeCode)) partyLabel = 'Received From';
      else if (['SI', 'NSI', 'PS', 'DC', 'SO', 'QT', 'SR', 'NSR'].includes(transaction.tranTypeCode)) partyLabel = 'Customer';
      else if (['PI', 'NPI', 'GRN', 'PO', 'PR', 'NPR'].includes(transaction.tranTypeCode)) partyLabel = 'Vendor';

      return `
      <div class="page">
        <div class="border-box">
          <div class="company-header">
            <div class="company-name">${activeCompany?.companyName || 'Company Name'}</div>
            <div class="company-address">
              ${activeCompany?.address || ''}${activeCompany?.city ? ', ' + activeCompany.city : ''}${activeCompany?.country ? ', ' + activeCompany.country : ''}
              ${activeCompany?.phone ? '<br>Phone: ' + activeCompany.phone : ''}${activeCompany?.email ? ' | Email: ' + activeCompany.email : ''}
            </div>
          </div>
          <div class="voucher-title"><h2>${voucherTitle}</h2></div>
          <div class="voucher-meta">
            <div class="left">
              <div><span class="meta-label">Voucher No:</span> <span class="meta-value">${transaction.docNumber || '-'}</span></div>
              ${transaction.jobName ? `<div><span class="meta-label">Job:</span> <span class="meta-value">${transaction.jobName}</span></div>` : ''}
              ${transaction.employeeName ? `<div><span class="meta-label">Employee:</span> <span class="meta-value">${transaction.employeeName}</span></div>` : ''}
            </div>
            <div class="right">
              <div><span class="meta-label">Date:</span> <span class="meta-value">${formatDate(transaction.docDate)}</span></div>
              <div><span class="meta-label">Status:</span> <span class="meta-value">${transaction.status || '-'}</span></div>
            </div>
          </div>
          ${transaction.partyName ? `<div class="party-section"><div class="party-label">${partyLabel}</div><div class="party-name">${transaction.partyName}</div></div>` : ''}

          ${transaction.lines && transaction.lines.length > 0 ? `
          <div class="items-section">
            <h3>Item Details</h3>
            <table class="items">
              <thead><tr>
                <th>#</th><th>Code</th><th>Item</th><th class="amount">Qty</th>
                <th class="amount">Unit Price</th><th class="amount">Disc</th>
                <th class="amount">${taxLabel}</th><th class="amount">Amount</th>
              </tr></thead>
              <tbody>
                ${transaction.lines.map((line, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${line.itemCode || '-'}</td>
                  <td>${line.itemName || '-'}</td>
                  <td class="amount">${line.quantity != null ? Number(line.quantity).toLocaleString() : '-'}</td>
                  <td class="amount">${formatAmount(line.unitPrice)}</td>
                  <td class="amount">${line.discountAmount ? formatAmount(line.discountAmount) : '-'}</td>
                  <td class="amount">${line.gstAmount ? formatAmount(line.gstAmount) : '-'}</td>
                  <td class="amount">${formatAmount(line.amount)}</td>
                </tr>`).join('')}
              </tbody>
              <tfoot><tr>
                <td colspan="3" style="text-align:right;">Totals</td>
                <td class="amount">${transaction.lines.reduce((s, l) => s + (l.quantity || 0), 0).toLocaleString()}</td>
                <td></td>
                <td class="amount">${transaction.totalDiscount ? formatAmount(transaction.totalDiscount) : '-'}</td>
                <td class="amount">${transaction.totalGST ? formatAmount(transaction.totalGST) : '-'}</td>
                <td class="amount">${formatAmount(transaction.totalAmount)}</td>
              </tr></tfoot>
            </table>
          </div>` : ''}

          <table class="entries">
            <thead><tr>
              <th>A/C Code</th><th>Account Name</th><th>Description</th>
              <th class="amount">Debit (${currencySymbol})</th><th class="amount">Credit (${currencySymbol})</th>
            </tr></thead>
            <tbody>
              ${subs.length > 0 ? subs.map(st => `
              <tr>
                <td>${st.accountCode || '-'}</td><td>${st.accountName || '-'}</td>
                <td>${st.description || '-'}</td>
                <td class="amount">${st.debitAmount > 0 ? formatAmount(st.debitAmount) : '-'}</td>
                <td class="amount">${st.creditAmount > 0 ? formatAmount(st.creditAmount) : '-'}</td>
              </tr>`).join('') : '<tr><td colspan="5" style="text-align:center;color:#999;padding:20px;">No entries</td></tr>'}
            </tbody>
            ${subs.length > 0 ? `<tfoot><tr class="totals-row">
              <td colspan="3" style="text-align:right;">Total</td>
              <td class="amount">${formatAmount(totalDebit)}</td>
              <td class="amount">${formatAmount(totalCredit)}</td>
            </tr></tfoot>` : ''}
          </table>

          <div class="amount-words">
            <div class="label">Amount in Words</div>
            <div class="text">Rupees ${numberToWords(primaryAmount)}</div>
          </div>
          ${transaction.notes ? `<div class="remarks-section"><div class="remarks-label">Remarks</div><div class="remarks-text">${transaction.notes}</div></div>` : ''}
          <div class="signatures">
            <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Prepared By</div></div>
            <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Checked By</div></div>
            <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Approved By</div></div>
            <div class="signature-block"><div class="signature-line"></div><div class="signature-label">Received By</div></div>
          </div>
          <div class="footer-note">Computer-generated voucher. Printed: ${new Date().toLocaleDateString('en-PK', { day: '2-digit', month: 'long', year: 'numeric' })}.</div>
        </div>
      </div>`;
    }).join('');

    w.document.write(`<!DOCTYPE html><html><head><title>Bulk Print - ${transList.length} Vouchers</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', sans-serif; color: #1A1A1A; font-size: 13px; background: #f5f5f5; }
        .page { width: 210mm; min-height: 297mm; margin: 0 auto 10px; background: white; padding: 20mm 18mm; position: relative; page-break-after: always; }
        .page:last-child { page-break-after: auto; }
        .border-box { border: 2px solid #800020; padding: 20px; min-height: calc(297mm - 44mm); }
        .company-header { text-align: center; margin-bottom: 18px; padding-bottom: 14px; border-bottom: 2px solid #800020; }
        .company-name { font-size: 22px; font-weight: 700; color: #800020; letter-spacing: 1px; margin-bottom: 4px; }
        .company-address { font-size: 11px; color: #666; line-height: 1.5; }
        .voucher-title { text-align: center; margin: 18px 0 20px; }
        .voucher-title h2 { font-size: 17px; font-weight: 700; padding: 6px 20px; display: inline-block; border: 1.5px solid #800020; background: #800020; color: white; letter-spacing: 2px; }
        .voucher-meta { display: flex; justify-content: space-between; margin-bottom: 18px; font-size: 13px; }
        .voucher-meta .left, .voucher-meta .right { display: flex; flex-direction: column; gap: 4px; }
        .voucher-meta .right { text-align: right; }
        .meta-label { font-weight: 600; color: #333; }
        .meta-value { color: #555; }
        .party-section { margin-bottom: 18px; padding: 10px 14px; background: #fafafa; border: 1px solid #eee; border-radius: 4px; }
        .party-label { font-size: 11px; font-weight: 600; color: #800020; text-transform: uppercase; }
        .party-name { font-size: 15px; font-weight: 600; color: #1A1A1A; margin-top: 2px; }
        .items-section { margin-bottom: 18px; }
        .items-section h3 { font-size: 13px; font-weight: 600; color: #800020; margin-bottom: 8px; text-transform: uppercase; }
        table.items { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
        table.items th { background: #f0e0e0; color: #800020; padding: 7px 10px; text-align: left; font-size: 10px; font-weight: 600; text-transform: uppercase; border-bottom: 1.5px solid #800020; }
        table.items th.amount { text-align: right; }
        table.items td { padding: 6px 10px; border-bottom: 1px solid #eee; font-size: 11px; }
        table.items td.amount { text-align: right; font-family: 'Consolas', monospace; }
        table.items tfoot td { font-weight: 700; border-top: 1.5px solid #800020; font-size: 12px; padding: 8px 10px; }
        table.entries { width: 100%; border-collapse: collapse; margin-bottom: 18px; }
        table.entries th { background: #800020; color: white; padding: 9px 12px; text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; }
        table.entries th.amount { text-align: right; }
        table.entries td { padding: 8px 12px; border-bottom: 1px solid #e0e0e0; font-size: 12px; }
        table.entries td.amount { text-align: right; font-family: 'Consolas', monospace; }
        .totals-row td { font-weight: 700; font-size: 13px; border-top: 2px solid #800020; padding: 10px 12px; background: #f5f0f0; }
        .amount-words { margin: 16px 0; padding: 10px 14px; background: #f9f5f5; border: 1px solid #e0d0d0; border-radius: 4px; }
        .amount-words .label { font-size: 11px; font-weight: 600; color: #800020; text-transform: uppercase; }
        .amount-words .text { font-size: 13px; font-weight: 500; color: #333; margin-top: 2px; font-style: italic; }
        .remarks-section { margin-bottom: 20px; }
        .remarks-label { font-size: 11px; font-weight: 600; color: #800020; text-transform: uppercase; }
        .remarks-text { font-size: 12px; color: #555; margin-top: 4px; }
        .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
        .signature-block { text-align: center; min-width: 130px; }
        .signature-line { border-top: 1.5px solid #333; margin-bottom: 6px; width: 130px; }
        .signature-label { font-size: 11px; color: #666; }
        .footer-note { text-align: center; margin-top: 30px; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 10px; }
        @media print { body { background: white; } .page { margin: 0; box-shadow: none; padding: 15mm; } .no-print { display: none; } }
        @media screen { .page { box-shadow: 0 2px 20px rgba(0,0,0,0.15); margin: 20px auto; } .print-bar { text-align: center; padding: 12px; background: #800020; position: sticky; top: 0; z-index: 100; }
          .print-bar button { padding: 8px 24px; font-size: 14px; font-weight: 600; color: white; background: transparent; border: 2px solid white; border-radius: 4px; cursor: pointer; margin: 0 6px; }
          .print-bar button:hover { background: rgba(255,255,255,0.15); } .print-bar span { color: white; font-size: 13px; margin: 0 10px; } }
      </style></head><body>
      <div class="print-bar no-print">
        <button onclick="window.print()">Print All (${transList.length})</button>
        <span>${transList.length} Vouchers</span>
        <button onclick="window.close()">Close</button>
      </div>
      ${voucherPages}
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
  };

  // ── CSV Export ──
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    let csv = 'Vouchers Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || 'All'}\n`;
    csv += `Date Range: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Voucher Type: ${voucherType === 'all' ? 'All' : TRAN_TYPE_LABELS[voucherType] || voucherType}\n\n`;
    csv += `Date,Doc Number,Type,Party,Employee,Job,Amount,${taxLabel},WHT,Discount,Net Amount,Status,Remarks\n`;

    filteredTransactions.forEach(t => {
      csv += `"${formatDate(t.docDate)}","${t.docNumber || ''}","${TRAN_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || ''}",`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.employeeName || '').replace(/"/g, '""')}","${(t.jobName || '').replace(/"/g, '""')}",${t.totalAmount || 0},${t.totalGST || 0},`;
      csv += `${t.totalWHT || 0},${t.totalDiscount || 0},${t.netAmount || t.totalAmount || 0},`;
      csv += `"${t.status || ''}","${(t.notes || '').replace(/"/g, '""')}"\n`;
    });

    csv += `\nTotal Vouchers:,${filteredTransactions.length}\n`;
    csv += `Total Amount:,${summary.netAmount}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Vouchers_${voucherType === 'all' ? 'All' : voucherType}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ══════════════════════════════════════════
  // ██  RENDER
  // ══════════════════════════════════════════
  return (
    <PageLayout pageTitle="Vouchers" breadcrumb="Reports / Accounting Reports / Vouchers">
      {/* ── Summary Cards ── */}
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}><FiFileText /></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Vouchers</SummaryLabel>
            <SummaryValue>{summary.totalVouchers}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#1565C0"><span style={{ fontSize: 16, fontWeight: 700 }}>Dr</span></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Debit</SummaryLabel>
            <SummaryValue style={{ color: '#1565C0' }}>{formatAmount(summary.totalDebit)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color="#C62828"><span style={{ fontSize: 16, fontWeight: 700 }}>Cr</span></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total Credit</SummaryLabel>
            <SummaryValue style={{ color: '#C62828' }}>{formatAmount(summary.totalCredit)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.success.main}><span style={{ fontSize: 14, fontWeight: 700 }}>{currencySymbol}</span></SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Net Amount</SummaryLabel>
            <SummaryValue>{formatAmount(summary.netAmount)}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
      </SummaryCards>

      {/* ── Filters ── */}
      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}{fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>From Date</Label>
            <DateInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>To Date</Label>
            <DateInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>
          <FilterGroup>
            <Label>Voucher Type</Label>
            <Select value={voucherType} onChange={e => setVoucherType(e.target.value)}>
              {VOUCHER_FILTER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Tax Filter</Label>
            <Select value={taxFilter} onChange={e => setTaxFilter(e.target.value)}>
              <option value="both">Both (Tax + Non-Tax)</option>
              <option value="tax">Tax Only</option>
              <option value="nonTax">Non-Tax Only</option>
            </Select>
          </FilterGroup>
        </FiltersRow>
        <FiltersRow style={{ marginTop: 14 }}>
          <SearchInputWrap>
            <SearchIcon><FiSearch /></SearchIcon>
            <Input
              type="text"
              placeholder="Search by doc number, party name, remarks..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </SearchInputWrap>
          <ButtonGroup>
            <PrimaryButton onClick={generateReport} disabled={!selectedFYId || loading}>
              <FiFileText /> {loading ? 'Loading...' : 'Generate'}
            </PrimaryButton>
            {selectedIds.size > 0 && (
              <PrimaryButton onClick={handleBulkPrint} disabled={loadingSubTrans}>
                <FiPrinter /> Print Selected ({selectedIds.size})
              </PrimaryButton>
            )}
            <Button onClick={handleExport}><FiDownload /> CSV</Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      {/* ── Table ── */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <Spinner />
              <div style={{ marginTop: 12 }}>Loading vouchers...</div>
            </EmptyState>
          ) : !selectedFYId ? (
            <EmptyState>
              <FiCalendar size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Please select a financial year to view vouchers.</div>
            </EmptyState>
          ) : !hasLoaded ? (
            <EmptyState>
              <FiFileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>Select a financial year and click "Generate" to load vouchers.</div>
            </EmptyState>
          ) : filteredTransactions.length === 0 ? (
            <EmptyState>
              <FiFileText size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
              <div>{transactions.length === 0 ? 'No transactions found for this financial year.' : 'No vouchers match your filters.'}</div>
            </EmptyState>
          ) : (
            <>
              <Table>
                <thead>
                  <Tr>
                    <Th style={{ width: 40 }}>
                      <CheckboxWrap onClick={toggleSelectAll}>
                        {allOnPageSelected ? <FiCheckSquare size={16} /> : <FiSquare size={16} />}
                      </CheckboxWrap>
                    </Th>
                    <Th>Date</Th>
                    <Th>Doc #</Th>
                    <Th>Type</Th>
                    <Th>Party</Th>
                    <Th>Employee</Th>
                    <Th align="right">Amount</Th>
                    <Th style={{ width: 100 }}>Status</Th>
                    <Th style={{ width: 110 }} align="center">Actions</Th>
                  </Tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map(t => {
                    const sts = getStatusStyle(t.status);
                    return (
                      <DataRow key={t.id} onClick={() => handleViewVoucher(t)}>
                        <Td onClick={e => e.stopPropagation()}>
                          <CheckboxWrap onClick={() => toggleSelect(t.id)}>
                            {selectedIds.has(t.id) ? <FiCheckSquare size={16} style={{ color: colors.primary.main }} /> : <FiSquare size={16} />}
                          </CheckboxWrap>
                        </Td>
                        <Td>{formatDate(t.docDate)}</Td>
                        <Td onClick={e => e.stopPropagation()}>
                          <DocNumber
                            style={{ cursor: 'pointer', color: '#1a73e8' }}
                            onClick={() => navigateToTransaction(navigate, t.tranTypeCode, t.id)}
                            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                          >
                            {t.docNumber || '-'} <FiExternalLink size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                          </DocNumber>
                        </Td>
                        <Td><TranBadge $type={t.tranTypeCode}>{TRAN_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || '-'}</TranBadge></Td>
                        <Td><PartyName>{t.partyName || '-'}</PartyName></Td>
                        <Td style={{ fontSize: 12, color: colors.text.secondary }}>{t.employeeName || '-'}</Td>
                        <AmountTd align="right">{formatAmount(t.netAmount || t.totalAmount)}</AmountTd>
                        <Td>
                          <StatusBadge style={{ background: sts.bg, color: sts.color }}>{t.status || '-'}</StatusBadge>
                        </Td>
                        <Td align="center" onClick={e => e.stopPropagation()}>
                          <ActionGroup>
                            <ActionBtn title="View Voucher" onClick={() => handleViewVoucher(t)}>
                              <FiEye size={15} />
                            </ActionBtn>
                            <ActionBtn title="Print Voucher" onClick={() => printVoucher(t)} $primary>
                              <FiPrinter size={15} />
                            </ActionBtn>
                          </ActionGroup>
                        </Td>
                      </DataRow>
                    );
                  })}
                </tbody>
              </Table>

              {/* ── Pagination ── */}
              {totalPages > 1 && (
                <PaginationBar>
                  <PaginationInfo>
                    Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, filteredTransactions.length)} of {filteredTransactions.length} vouchers
                  </PaginationInfo>
                  <PaginationButtons>
                    <PagBtn disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                      <FiChevronLeft size={16} />
                    </PagBtn>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 7) {
                        pageNum = i + 1;
                      } else if (currentPage <= 4) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 3) {
                        pageNum = totalPages - 6 + i;
                      } else {
                        pageNum = currentPage - 3 + i;
                      }
                      return (
                        <PagBtn key={pageNum} $active={currentPage === pageNum} onClick={() => setCurrentPage(pageNum)}>
                          {pageNum}
                        </PagBtn>
                      );
                    })}
                    <PagBtn disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                      <FiChevronRight size={16} />
                    </PagBtn>
                  </PaginationButtons>
                </PaginationBar>
              )}
            </>
          )}
        </TableContainer>
      </ContentCard>

      {/* ══════════ Voucher Detail Modal ══════════ */}
      {viewVoucher && (
        <ModalOverlay onClick={() => setViewVoucher(null)}>
          <ModalContainer onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <TranBadge $type={viewVoucher.tranTypeCode} style={{ fontSize: 12, marginRight: 8 }}>
                  {TRAN_TYPE_LABELS[viewVoucher.tranTypeCode] || viewVoucher.tranTypeCode}
                </TranBadge>
                {viewVoucher.docNumber || 'Voucher Detail'}
              </ModalTitle>
              <ModalActions>
                <Button onClick={() => printVoucher(viewVoucher)}><FiPrinter /> Print</Button>
                <CloseBtn onClick={() => setViewVoucher(null)}><FiX size={20} /></CloseBtn>
              </ModalActions>
            </ModalHeader>
            <ModalBody>
              <DetailGrid>
                <DetailRow>
                  <DetailLabel>Document #</DetailLabel>
                  <DetailValue>{viewVoucher.docNumber || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Date</DetailLabel>
                  <DetailValue>{formatDate(viewVoucher.docDate)}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Type</DetailLabel>
                  <DetailValue>{TRAN_TYPE_LABELS[viewVoucher.tranTypeCode] || viewVoucher.tranTypeCode || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    <StatusBadge style={{ ...getStatusStyle(viewVoucher.status), background: getStatusStyle(viewVoucher.status).bg, color: getStatusStyle(viewVoucher.status).color }}>
                      {viewVoucher.status || '-'}
                    </StatusBadge>
                  </DetailValue>
                </DetailRow>
                {viewVoucher.partyName && (
                  <DetailRow>
                    <DetailLabel>Party</DetailLabel>
                    <DetailValue style={{ fontWeight: 600 }}>{viewVoucher.partyName}</DetailValue>
                  </DetailRow>
                )}
                {viewVoucher.salesPersonName && (
                  <DetailRow>
                    <DetailLabel>Sales Person</DetailLabel>
                    <DetailValue>{viewVoucher.salesPersonName}</DetailValue>
                  </DetailRow>
                )}
                {viewVoucher.employeeName && (
                  <DetailRow>
                    <DetailLabel>Employee</DetailLabel>
                    <DetailValue>{viewVoucher.employeeName}</DetailValue>
                  </DetailRow>
                )}
                {viewVoucher.jobName && (
                  <DetailRow>
                    <DetailLabel>Job/Project</DetailLabel>
                    <DetailValue>{viewVoucher.jobName}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>Total Amount</DetailLabel>
                  <DetailValue style={{ fontWeight: 700, fontSize: 16 }}>{currencySymbol} {formatAmount(viewVoucher.netAmount || viewVoucher.totalAmount)}</DetailValue>
                </DetailRow>
                {viewVoucher.totalGST > 0 && (
                  <DetailRow>
                    <DetailLabel>{taxLabel}</DetailLabel>
                    <DetailValue>{currencySymbol} {formatAmount(viewVoucher.totalGST)}</DetailValue>
                  </DetailRow>
                )}
                {viewVoucher.totalWHT > 0 && (
                  <DetailRow>
                    <DetailLabel>WHT</DetailLabel>
                    <DetailValue>{currencySymbol} {formatAmount(viewVoucher.totalWHT)}</DetailValue>
                  </DetailRow>
                )}
                {viewVoucher.totalDiscount > 0 && (
                  <DetailRow>
                    <DetailLabel>Discount</DetailLabel>
                    <DetailValue>{currencySymbol} {formatAmount(viewVoucher.totalDiscount)}</DetailValue>
                  </DetailRow>
                )}
                {viewVoucher.notes && (
                  <DetailRow $full>
                    <DetailLabel>Remarks</DetailLabel>
                    <DetailValue>{viewVoucher.notes}</DetailValue>
                  </DetailRow>
                )}
              </DetailGrid>

              {/* Item lines if present */}
              {viewVoucher.lines && viewVoucher.lines.length > 0 && (
                <ModalSection>
                  <ModalSectionTitle>Item Lines</ModalSectionTitle>
                  <ModalTable>
                    <thead>
                      <Tr>
                        <Th>#</Th>
                        <Th>Code</Th>
                        <Th>Item</Th>
                        <Th align="right">Qty</Th>
                        <Th align="right">Price</Th>
                        <Th align="right">Amount</Th>
                      </Tr>
                    </thead>
                    <tbody>
                      {viewVoucher.lines.map((line, i) => (
                        <Tr key={i}>
                          <Td>{i + 1}</Td>
                          <Td><code style={{ fontSize: 11, color: colors.text.secondary }}>{line.itemCode || '-'}</code></Td>
                          <Td>{line.itemName || '-'}</Td>
                          <AmountTd align="right">{line.quantity != null ? Number(line.quantity).toLocaleString() : '-'}</AmountTd>
                          <AmountTd align="right">{formatAmount(line.unitPrice)}</AmountTd>
                          <AmountTd align="right">{formatAmount(line.amount)}</AmountTd>
                        </Tr>
                      ))}
                    </tbody>
                  </ModalTable>
                </ModalSection>
              )}

              {/* Accounting entries */}
              <ModalSection>
                <ModalSectionTitle>Accounting Entries</ModalSectionTitle>
                {loadingSubTrans && !subTransactionsMap[viewVoucher.id] ? (
                  <div style={{ textAlign: 'center', padding: 20, color: colors.text.secondary }}>
                    <Spinner /> Loading entries...
                  </div>
                ) : (subTransactionsMap[viewVoucher.id] || []).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 20, color: colors.text.secondary }}>
                    No accounting entries found for this voucher.
                  </div>
                ) : (
                  <ModalTable>
                    <thead>
                      <Tr>
                        <Th>A/C Code</Th>
                        <Th>Account</Th>
                        <Th>Description</Th>
                        <Th align="right">Debit</Th>
                        <Th align="right">Credit</Th>
                      </Tr>
                    </thead>
                    <tbody>
                      {(subTransactionsMap[viewVoucher.id] || []).map((st, i) => (
                        <Tr key={i}>
                          <Td><code style={{ fontSize: 11, color: colors.text.secondary }}>{st.accountCode || '-'}</code></Td>
                          <Td>{st.accountName || '-'}</Td>
                          <Td style={{ fontSize: 12, color: colors.text.secondary }}>{st.description || '-'}</Td>
                          <AmountTd align="right" $debit>{st.debitAmount > 0 ? formatAmount(st.debitAmount) : '-'}</AmountTd>
                          <AmountTd align="right" $credit>{st.creditAmount > 0 ? formatAmount(st.creditAmount) : '-'}</AmountTd>
                        </Tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <TotalRow>
                        <Td colSpan={3} style={{ textAlign: 'right', fontWeight: 700 }}>Total</Td>
                        <AmountTd align="right" $debit style={{ fontWeight: 700 }}>
                          {formatAmount((subTransactionsMap[viewVoucher.id] || []).reduce((s, st) => s + (st.debitAmount || 0), 0))}
                        </AmountTd>
                        <AmountTd align="right" $credit style={{ fontWeight: 700 }}>
                          {formatAmount((subTransactionsMap[viewVoucher.id] || []).reduce((s, st) => s + (st.creditAmount || 0), 0))}
                        </AmountTd>
                      </TotalRow>
                    </tfoot>
                  </ModalTable>
                )}
              </ModalSection>

              {/* Amount in words */}
              <AmountWordsBox>
                <span style={{ fontWeight: 600, color: colors.primary.main, fontSize: 11, textTransform: 'uppercase' }}>Amount in Words: </span>
                <span style={{ fontStyle: 'italic', color: colors.text.primary }}>
                  Rupees {numberToWords(viewVoucher.netAmount || viewVoucher.totalAmount || 0)}
                </span>
              </AmountWordsBox>
            </ModalBody>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// ══════════════════════════════════════════════
// ██  STYLED COMPONENTS
// ══════════════════════════════════════════════

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius?.md || '6px'};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const SummaryInfo = styled.div``;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

// ── Filters ──

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 150px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DateInput = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px 9px 38px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  color: ${colors.text.primary};
  &:focus { outline: none; border-color: ${colors.primary.main}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  color: ${colors.text.primary};
  transition: all 0.2s;
  &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
`;

const PrimaryButton = styled(Button)`
  background: ${colors.primary.main};
  color: white;
  border-color: ${colors.primary.main};
  &:hover { background: ${colors.primary.dark}; border-color: ${colors.primary.dark}; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// ── Table ──

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: ${colors.shadows?.sm || '0 1px 3px rgba(0,0,0,0.1)'};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ModalTable = styled(Table)`
  margin-bottom: 0;
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.align || 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const DataRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;
  &:hover { background: ${colors.neutral[50]}; }
`;

const TotalRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};
  td { padding: 14px; }
`;

const AmountTd = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  color: ${props => props.$debit ? '#1565C0' : props.$credit ? '#C62828' : colors.text.primary};
  font-weight: 500;
`;

const DocNumber = styled.span`
  font-weight: 600;
  color: ${colors.primary.main};
  font-size: 13px;
`;

const PartyName = styled.span`
  font-size: 13px;
  color: ${colors.text.primary};
  max-width: 200px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TranBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props => {
    const t = props.$type;
    if (t === 'PI' || t === 'PR' || t === 'GRN' || t === 'PO') return '#E3F2FD';
    if (t === 'SI' || t === 'PS' || t === 'SR' || t === 'DC' || t === 'SO' || t === 'QT') return '#FFF3E0';
    if (t === 'CR' || t === 'BR') return '#E8F5E9';
    if (t === 'CP' || t === 'BP') return '#FFEBEE';
    if (t === 'JV' || t === 'OB') return '#F3E5F5';
    if (t === 'AS' || t === 'RS' || t === 'ST') return '#E0F7FA';
    return colors.neutral[100];
  }};
  color: ${props => {
    const t = props.$type;
    if (t === 'PI' || t === 'PR' || t === 'GRN' || t === 'PO') return '#1565C0';
    if (t === 'SI' || t === 'PS' || t === 'SR' || t === 'DC' || t === 'SO' || t === 'QT') return '#E65100';
    if (t === 'CR' || t === 'BR') return '#2E7D32';
    if (t === 'CP' || t === 'BP') return '#C62828';
    if (t === 'JV' || t === 'OB') return '#7B1FA2';
    if (t === 'AS' || t === 'RS' || t === 'ST') return '#00838F';
    return colors.text.secondary;
  }};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const CheckboxWrap = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  &:hover { color: ${colors.primary.main}; }
`;

// ── Actions ──

const ActionGroup = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

const ActionBtn = styled.button`
  padding: 6px 8px;
  border: 1px solid ${props => props.$primary ? colors.primary.main : colors.neutral[300]};
  border-radius: ${colors.borderRadius?.md || '6px'};
  background: ${props => props.$primary ? colors.primary.main : 'white'};
  color: ${props => props.$primary ? 'white' : colors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  &:hover {
    background: ${props => props.$primary ? colors.primary.dark : colors.neutral[100]};
    border-color: ${colors.primary.main};
    color: ${props => props.$primary ? 'white' : colors.primary.main};
  }
`;

// ── Pagination ──

const PaginationBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid ${colors.neutral[200]};
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 4px;
`;

const PagBtn = styled.button`
  padding: 6px 10px;
  border: 1px solid ${props => props.$active ? colors.primary.main : colors.neutral[300]};
  border-radius: 4px;
  background: ${props => props.$active ? colors.primary.main : 'white'};
  color: ${props => props.$active ? 'white' : colors.text.primary};
  font-size: 12px;
  font-weight: ${props => props.$active ? 600 : 400};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: ${colors.primary.main}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ── Modal ──

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 500;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${colors.borderRadius?.lg || '8px'};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 850px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 2px solid ${colors.primary.main};
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;
`;

const ModalTitle = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const CloseBtn = styled.button`
  padding: 6px;
  background: transparent;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: all 0.2s;
  &:hover { color: ${colors.error.main}; background: ${colors.error.main}15; }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 24px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  ${props => props.$full && 'grid-column: 1 / -1;'}
`;

const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: ${colors.text.primary};
`;

const ModalSection = styled.div`
  margin-bottom: 20px;
`;

const ModalSectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.primary.main};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const AmountWordsBox = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: ${colors.primary.main}08;
  border: 1px solid ${colors.primary.main}20;
  border-radius: 6px;
  font-size: 13px;
`;

// ── Loading ──

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Spinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${colors.neutral[200]};
  border-top-color: ${colors.primary.main};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export default Vouchers;
